export interface InstallPopupResult {
    ok: boolean;
    /** Every URL param the vendor's redirect-back carried (state, installation_id, etc). */
    params?: Record<string, string>;
    error?: string;
    cancelled?: boolean;
}

export type InstallMessage =
    | { type: 'install:complete'; params: Record<string, string> }
    | { type: 'install:error'; error: string };

export interface InstallPopupHandle {
    result: Promise<InstallPopupResult>;
    close: () => void;
}

const POPUP_FEATURES = 'width=820,height=820,menubar=no,toolbar=no,location=no,status=no';

export function openInstallPopup(url: string): InstallPopupHandle {
    const popup = window.open(url, 'integrations-install', POPUP_FEATURES);
    if (!popup) {
        return {
            result: Promise.resolve({
                ok: false,
                error: 'Popup was blocked — please allow popups for this site and try again.',
            }),
            close: (): void => {},
        };
    }

    let settled = false;
    let cancelledByCaller = false;

    const result = new Promise<InstallPopupResult>((resolve) => {
        const settle = (r: InstallPopupResult): void => {
            if (settled) return;
            settled = true;
            window.removeEventListener('message', messageHandler);
            window.clearInterval(pollTimer);
            resolve(r);
        };

        const messageHandler = (event: MessageEvent): void => {
            if (event.source !== popup) return;
            if (event.origin !== window.location.origin) return;
            const data = event.data as InstallMessage | undefined;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'install:complete') {
                settle({ ok: true, params: data.params });
            } else if (data.type === 'install:error') {
                settle({ ok: false, error: data.error });
            }
        };

        const pollTimer = window.setInterval(() => {
            if (!popup.closed) return;
            if (cancelledByCaller) settle({ ok: false, cancelled: true });
            else settle({ ok: false, error: 'Install window was closed before completion.' });
        }, 500);

        window.addEventListener('message', messageHandler);
    });

    return {
        result,
        close: (): void => {
            cancelledByCaller = true;
            if (!popup.closed) popup.close();
        },
    };
}
